/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import * as moment from 'moment';

import { BaseHighChartComponent } from './base-chart.component';
import { ChartConfigService } from '../services/chart-config.service';

export interface ChartExtremes {
  min: number;
  max: number;
  trigger: string;
}

/**
 * Multi-line stock chart with a navigator slider and zoom→extremes output.
 *
 * Generic: it knows nothing about portfolios. Consumers feed `chartData`
 * (one series per line, each carrying its own `type`/`color`/`visible`) and
 * `navigatorData`, and react to `extremesChange` / `legendItemClick`. The
 * navigator date-range hover label is built here because it's intrinsic to the
 * navigator. Scanner/LLM charts are unaffected — they use the other components.
 */
@Component({
  selector: 'app-navigator-chart',
  template:
    '<div class="chart-container" style="height: 100%; width: 100%;"></div>',
})
export class NavigatorChartComponent extends BaseHighChartComponent {
  /** Aggregate series shown inside the navigator. */
  @Input() navigatorData: (number | null)[][] = [];
  /** `score` → raw values; `percent` → value × 100 with a `%` suffix (sentiment). */
  @Input() valueMode: 'score' | 'percent' = 'score';
  @Input() theme: 'light' | 'dark' = 'dark';
  /** Optional cap on the zoomable range, in milliseconds. */
  @Input() maxRangeMs?: number;
  @Input() showNavigator = true;

  @Output() extremesChange = new EventEmitter<ChartExtremes>();
  @Output() legendItemClick = new EventEmitter<void>();
  @Output() chartReady = new EventEmitter<Highcharts.Chart>();

  constructor(
    elementRef: ElementRef,
    protected configService: ChartConfigService,
  ) {
    super(elementRef, configService);
  }

  protected override getChartConfig(): Highcharts.Options {
    const isPercent = this.valueMode === 'percent';
    const isLight = this.theme === 'light';
    const emitLegendClick = () => this.legendItemClick.emit();
    // Fade a series in when it's toggled visible (Highcharts' default toggle is
    // instant). Runs after the default toggle, hence the deferral.
    const fadeInOnShow = (series: any) => {
      if (series && !series.visible) {
        setTimeout(() => {
          series.group
            ?.attr({ opacity: 0 })
            .animate({ opacity: 1 }, { duration: 400 });
        }, 0);
      }
    };

    return {
      chart: {
        backgroundColor: 'transparent',
        zooming: { type: 'x' },
        alignTicks: false,
      },
      credits: { enabled: false },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            symbol: 'menuball',
            width: 18,
            height: 18,
            symbolX: 9,
            symbolY: 9,
            symbolSize: 10,
            symbolStrokeWidth: 2,
            theme: { fill: 'none', stroke: 'none' },
          },
        },
      },
      rangeSelector: { enabled: false },
      legend: {
        enabled: true,
        maxHeight: 140,
        itemStyle: { color: isLight ? '#606063' : '#E0E0E3' },
        itemHoverStyle: { color: isLight ? '#606063' : '#E0E0E3' },
        itemHiddenStyle: { color: isLight ? '#E0E0E3' : '#606063' },
        navigation: { style: { color: isLight ? '#606063' : '#E0E0E3' } },
      },
      tooltip: {
        split: true,
        backgroundColor: 'rgba(0,0,0, 0.8)',
        pointFormatter: function () {
          if (!isPercent) {
            return (this as any).series.name + ' ' + (this as any).y;
          }
          const percent = ((this as any).y as number) * 100;
          return (this as any).series.name + ' ' + percent.toFixed(0) + '%';
        },
        style: { color: 'rgba(255,255,255, 1)' },
      },
      navigator: {
        enabled: this.showNavigator,
        handles: { backgroundColor: '#212121', borderColor: '#AAA' },
        outlineColor: 'transparent',
        outlineWidth: 0,
        maskFill: 'rgba(0,0,0,0.1)',
        series: {
          color: '#525252',
          lineColor: '#727272',
          data: this.navigatorData as any,
        },
        xAxis: { gridLineColor: '#525252' },
      },
      scrollbar: { enabled: false },
      xAxis: {
        type: 'datetime',
        gridLineColor: '#707073',
        labels: { style: { color: '#E0E0E3' } },
        lineColor: '#707073',
        minorGridLineColor: '#505053',
        tickColor: '#707073',
        title: { style: { color: '#A0A0A3' } },
        ...(this.maxRangeMs ? { maxRange: this.maxRangeMs } : {}),
        events: {
          afterSetExtremes: (event: any) =>
            this.extremesChange.emit({
              min: event.min,
              max: event.max,
              trigger: event.trigger,
            }),
        },
      },
      yAxis: [
        {
          opposite: true,
          labels: {
            formatter: function () {
              if (!isPercent) {
                return String((this as any).value);
              }
              return Math.round(((this as any).value as number) * 100) + '%';
            },
            align: 'left',
            style: { color: '#E0E0E3' },
          },
          title: { text: '', style: { color: '#A0A0A3' } },
          lineWidth: 2,
          gridLineColor: '#2D2D2D',
          lineColor: 'transparent',
        },
      ],
      plotOptions: {
        series: {
          events: {
            // Highcharts' default legend toggle just flips group visibility with
            // no animation. Fade the series in on show so a line "appears" instead
            // of popping in — without recreating the chart.
            legendItemClick: function (this: Highcharts.Series) {
              fadeInOnShow(this);
              emitLegendClick();
            },
          },
        },
      },
      series: this.chartData,
    };
  }

  protected override onChartCreated(): void {
    const chart = this.chart;
    if (!chart) {
      return;
    }
    this.chartReady.emit(chart);
    if (this.showNavigator) {
      this.attachNavigatorTooltip(chart as any);
    }
  }

  /**
   * Renders a "DD.MM.YYYY - DD.MM.YYYY" label above the navigator handles while
   * the user drags it. Ported verbatim from the legacy portfolio chart; uses the
   * Highcharts renderer (SVG), so it's independent of Angular view encapsulation.
   */
  private attachNavigatorTooltip(chart: any): void {
    const navContainer: HTMLElement | null = chart.container?.querySelector(
      '.highcharts-navigator',
    );
    if (!navContainer) {
      return;
    }

    let textObject: any;

    const calculatePosition = (
      navLeft: number,
      navRight: number,
      textSize: number,
      boxSize: number,
      boxPadding: number,
    ): number => {
      const positionLeft = Math.round((navLeft + navRight - textSize) * 0.5);
      const positionRight = Math.round((navLeft + navRight + textSize) * 0.5);
      if (positionLeft < 0) {
        return 0;
      } else if (positionRight > boxSize + boxPadding) {
        return boxSize + boxPadding - textSize;
      }
      return positionLeft;
    };

    const calculateDates = (
      dateMin: number,
      dateMax: number,
      navMin: number,
      navMax: number,
      navSize: number,
    ) => ({
      start: Math.ceil(dateMin + ((dateMax - dateMin) * navMin) / navSize),
      end: Math.floor(dateMin + ((dateMax - dateMin) * navMax) / navSize),
    });

    const destroyText = () => {
      if (textObject && Object.keys(textObject).length !== 0) {
        textObject.destroy();
      }
    };

    navContainer.addEventListener(
      'mousemove',
      () => {
        destroyText();
        const nav = chart.navigator;
        const dateRange = calculateDates(
          nav.xAxis.min,
          nav.xAxis.max,
          nav.zoomedMin,
          nav.zoomedMax,
          nav.size,
        );
        const label =
          moment(dateRange.start).format('DD.MM.YYYY') +
          ' - ' +
          moment(dateRange.end).format('DD.MM.YYYY');

        // First pass renders invisibly just to measure its width.
        textObject = chart.renderer
          .text(label, 0, 0, true)
          .attr({ id: 'navTooltip' })
          .css({ color: 'transparent' })
          .add();
        const measured = document.getElementById('navTooltip');
        const textWidth = measured ? measured.offsetWidth : 0;
        textObject.destroy();

        const textPositionX = calculatePosition(
          nav.handles[0].translateX,
          nav.handles[1].translateX,
          textWidth,
          nav.size,
          nav.left,
        );
        const themeColor = this.theme === 'light' ? '0, 0, 0' : '255, 255, 255';
        textObject = chart.renderer
          .text(label, textPositionX, nav.handles[0].translateY - 15)
          .css({ color: 'rgba(' + themeColor + ', 0.4)', fontSize: 12 })
          .add();
      },
      false,
    );

    navContainer.addEventListener('mouseleave', destroyText, false);
  }
}
